import {
  ANA_FUNNEL_TOUCH_AT_EVENT,
  getBaseTemplatePosthogDistinctId,
  getBaseTemplateSessionId,
  getBaseTemplateTrackedInteractionCount,
} from "@/lib/baseTemplateAnalytics";
import { mirrorFunnelEvent } from "@/lib/mirrorFunnelEvent";
import { useEffect, useRef } from "react";

const CLICKABLE_SELECTOR =
  'a[href], button, [role="button"], input[type="submit"], summary, label[for]';

const FORM_CONTROL_SELECTOR =
  "input:not([type=button]):not([type=submit]):not([type=reset]):not([type=hidden]), textarea, select";

const FIRST_LANDING_KEY = "anaa_funnel_first_landing_v1";

type EntryClassification = {
  entry_type: string;
  entry_detail: string;
  entry_label: string;
};

type StoredLanding = {
  pathname: string;
  href: string;
  entry: EntryClassification;
};

function formatDwell(totalSec: number): string {
  const s = Math.max(0, Math.floor(totalSec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  if (m <= 0) return `${r}s`;
  return `${m}m ${r}s`;
}

/** First-touch entrada (lost after client-side navigations strip query string). */
function detectEntry(): EntryClassification {
  try {
    const u = new URL(window.location.href);
    const q = u.searchParams;
    if (
      q.get("utm_source") ||
      q.get("utm_medium") ||
      q.get("utm_campaign") ||
      q.get("gclid") ||
      q.get("fbclid") ||
      q.get("twclid")
    ) {
      const src = q.get("utm_source") ?? "";
      const med = q.get("utm_medium") ?? "";
      const cmp = q.get("utm_campaign") ?? "";
      const detail =
        [src, med].filter(Boolean).join(" / ") ||
        [cmp].filter(Boolean).join(" ") ||
        "campaign";
      return { entry_type: "campaign", entry_detail: detail, entry_label: "Campaña" };
    }
    const ref = document.referrer?.trim();
    if (!ref) return { entry_type: "direct", entry_detail: "direct", entry_label: "Directo" };
    const host = new URL(ref).hostname.replace(/^www\./i, "");
    return { entry_type: "referral", entry_detail: host, entry_label: "Referido" };
  } catch {
    return { entry_type: "unknown", entry_detail: "-", entry_label: "Desconocido" };
  }
}

function readOrCreateLandingSnapshot(): StoredLanding {
  try {
    const raw = sessionStorage.getItem(FIRST_LANDING_KEY);
    if (raw) {
      const o = JSON.parse(raw) as Partial<StoredLanding>;
      if (o.pathname && o.href && o.entry?.entry_type) {
        return o as StoredLanding;
      }
    }
  } catch {
    /* stale storage */
  }
  const landing: StoredLanding = {
    pathname: window.location.pathname,
    href: window.location.href.slice(0, 2048),
    entry: detectEntry(),
  };
  try {
    sessionStorage.setItem(FIRST_LANDING_KEY, JSON.stringify(landing));
  } catch {
    /* private mode quota */
  }
  return landing;
}

function maxScrollPercent(): number {
  const el = document.documentElement;
  const sh = el.scrollHeight - el.clientHeight;
  if (sh <= 0) return 100;
  return Math.min(100, Math.round((el.scrollTop / sh) * 100));
}

function updateScrollMilestones(
  pct: number,
  m: { crossed_25: boolean; crossed_50: boolean; crossed_75: boolean; crossed_100: boolean },
): void {
  if (pct >= 25) m.crossed_25 = true;
  if (pct >= 50) m.crossed_50 = true;
  if (pct >= 75) m.crossed_75 = true;
  if (pct >= 98) m.crossed_100 = true;
}

function milestonesToLabel(m: {
  crossed_25: boolean;
  crossed_50: boolean;
  crossed_75: boolean;
  crossed_100: boolean;
}): string {
  const parts: string[] = [];
  if (m.crossed_25) parts.push("25");
  if (m.crossed_50) parts.push("50");
  if (m.crossed_75) parts.push("75");
  if (m.crossed_100) parts.push("100");
  return parts.length ? parts.join("+") : "—";
}

function getFcpMs(): number | null {
  try {
    const paints = performance.getEntriesByType("paint") as PerformanceEntry[];
    const fcp = paints.find((e) => e.name === "first-contentful-paint");
    return fcp?.startTime != null ? Math.round(fcp.startTime) : null;
  } catch {
    return null;
  }
}

function getConnectionSnapshot(): Record<string, unknown> | null {
  try {
    const c = (
      navigator as Navigator & {
        connection?: {
          effectiveType?: string;
          saveData?: boolean;
          rtt?: number;
          downlink?: number;
        };
      }
    ).connection;
    if (!c) return null;
    const out: Record<string, unknown> = {};
    if (typeof c.effectiveType === "string") out.effective_type = c.effectiveType;
    if (typeof c.saveData === "boolean") out.save_data = c.saveData;
    if (typeof c.rtt === "number" && Number.isFinite(c.rtt)) out.rtt_ms = Math.round(c.rtt);
    if (typeof c.downlink === "number" && Number.isFinite(c.downlink))
      out.downlink_mbps = Math.round(c.downlink * 10) / 10;
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

/**
 * On unload (`pagehide` / `beforeunload`), sends `base_template_passive_session_end` with
 * scroll depth, gallery exposure, entrada snapshot, SPA churn, device/network hints,
 * TTI-from-mount, funnel vs DOM gestures.
 *
 * Slack formats as a structured digest (once per funnel session — see server throttle).
 */
export function useBaseTemplatePassiveSessionSummary(opts: {
  templateName: string;
  countryCode?: string;
  language?: string;
  /** React-router pathname — SPA transitions without tab close. */
  pathname: string;
  /** Gallery item count/version — rescan observers when media set changes. */
  galleryKey: number;
}): void {
  const { templateName, countryCode, language, pathname, galleryKey } = opts;
  const sentRef = useRef(false);
  const startPerfRef = useRef<number>(
    typeof performance !== "undefined" ? performance.now() : 0,
  );
  const landingRef = useRef<StoredLanding | null>(null);
  const maxScrollRef = useRef(0);
  const milestonesRef = useRef({
    crossed_25: false,
    crossed_50: false,
    crossed_75: false,
    crossed_100: false,
  });
  const domClickRef = useRef(0);
  const keyboardEnterRef = useRef(0);
  const firstDomInteractPerfRef = useRef<number | null>(null);
  const firstFunnelTouchPerfRef = useRef<number | null>(null);
  /** Route changes excluding the landing route. */
  const spaTransitionsRef = useRef(0);
  const prevPathRef = useRef<string | null>(null);
  const gallerySeenRef = useRef<Set<Element>>(new Set());

  const mountFirstInteract = () => {
    if (firstDomInteractPerfRef.current !== null) return;
    if (typeof performance === "undefined") return;
    firstDomInteractPerfRef.current = performance.now();
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    getBaseTemplateSessionId();
    landingRef.current = readOrCreateLandingSnapshot();

    let scrollRaf = 0;
    const onScroll = () => {
      cancelAnimationFrame(scrollRaf);
      scrollRaf = requestAnimationFrame(() => {
        const pct = maxScrollPercent();
        maxScrollRef.current = Math.max(maxScrollRef.current, pct);
        updateScrollMilestones(pct, milestonesRef.current);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();

    const onClick = (ev: MouseEvent) => {
      const t = ev.target;
      if (!(t instanceof Element)) return;
      if (t.closest(CLICKABLE_SELECTOR)) {
        domClickRef.current += 1;
        mountFirstInteract();
      }
    };
    document.addEventListener("click", onClick, true);

    const onKey = (ev: KeyboardEvent) => {
      if (ev.key !== "Enter") return;
      const t = ev.target;
      if (!(t instanceof HTMLElement)) return;
      if (t.closest(FORM_CONTROL_SELECTOR) || t.closest(CLICKABLE_SELECTOR)) {
        keyboardEnterRef.current += 1;
        mountFirstInteract();
      }
    };
    document.addEventListener("keydown", onKey, true);

    const onFunnelTouch = (ev: Event) => {
      const ce = ev as CustomEvent<number>;
      if (typeof ce.detail !== "number" || !Number.isFinite(ce.detail)) return;
      mountFirstInteract();
      const prev = firstFunnelTouchPerfRef.current;
      if (prev === null || ce.detail < prev) firstFunnelTouchPerfRef.current = ce.detail;
    };
    window.addEventListener(ANA_FUNNEL_TOUCH_AT_EVENT, onFunnelTouch);

    return () => {
      cancelAnimationFrame(scrollRaf);
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("keydown", onKey, true);
      window.removeEventListener(ANA_FUNNEL_TOUCH_AT_EVENT, onFunnelTouch);
    };
  }, []);

  useEffect(() => {
    if (prevPathRef.current === null) {
      prevPathRef.current = pathname;
      return;
    }
    if (prevPathRef.current !== pathname) {
      spaTransitionsRef.current += 1;
      prevPathRef.current = pathname;
    }
  }, [pathname]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") return;

    const observed = new WeakSet<Element>();

    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting && e.intersectionRatio >= 0.3) gallerySeenRef.current.add(e.target);
        }
      },
      { root: null, threshold: [0, 0.3, 0.6] },
    );

    const scan = () => {
      document.querySelectorAll(".gallery-grid-item").forEach((el) => {
        if (!observed.has(el)) {
          observed.add(el);
          observer.observe(el);
        }
      });
    };

    scan();
    let raf = 0;
    const mo = new MutationObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(scan);
    });
    mo.observe(document.body, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      mo.disconnect();
      observer.disconnect();
    };
  }, [galleryKey]);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const buildPayload = (
      exitReason: string,
      exitMeta?: { persisted_bfcache?: boolean },
    ): Record<string, unknown> => {
      const baseline = startPerfRef.current;
      const nowPerf = typeof performance !== "undefined" ? performance.now() : Date.now();

      let ttiMs: number | null = null;
      if (typeof performance !== "undefined") {
        const domAt = firstDomInteractPerfRef.current;
        const funAt = firstFunnelTouchPerfRef.current;
        const candidates = [domAt, funAt].filter(
          (x): x is number => x != null && x >= baseline,
        );
        if (candidates.length) {
          const first = Math.min(...candidates);
          ttiMs = Math.round(first - baseline);
        }
      }

      const dwellSec = (nowPerf - baseline) / 1000;
      const scrollPct = Math.max(maxScrollRef.current, maxScrollPercent());
      updateScrollMilestones(scrollPct, milestonesRef.current);
      const m = milestonesRef.current;
      const landing = landingRef.current;
      const entry = landing?.entry ?? detectEntry();

      const navLang = typeof navigator !== "undefined" ? navigator.language : "";
      const bodyLang = language?.trim() || navLang;
      const conn = getConnectionSnapshot();
      const netType =
        conn && typeof conn.effective_type === "string" ? String(conn.effective_type) : null;
      const fcpMs = getFcpMs();

      const rawUiInteractions = domClickRef.current + keyboardEnterRef.current;

      return {
        event_name: "base_template_passive_session_end",
        posthog_distinct_id: getBaseTemplatePosthogDistinctId(),
        session_id: getBaseTemplateSessionId(),
        template: templateName,
        flow_name: "passive_exit",
        landing_path: window.location.pathname,
        country: countryCode,
        language: bodyLang.slice(0, 8),
        page_url: window.location.href,
        props: {
          passive_exit: true,
          exit_reason: exitReason,
          exit_persisted_bfcache: exitMeta?.persisted_bfcache ?? false,
          dwell_sec: Math.round(dwellSec),
          dwell_human: formatDwell(dwellSec),
          max_scroll_pct: scrollPct,
          scroll_milestones_label: milestonesToLabel(m),
          scroll_crossed_25: m.crossed_25,
          scroll_crossed_50: m.crossed_50,
          scroll_crossed_75: m.crossed_75,
          scroll_crossed_100: m.crossed_100,
          gallery_cards_seen: gallerySeenRef.current.size,
          dom_click_count: domClickRef.current,
          keyboard_enter_count: keyboardEnterRef.current,
          funnel_tracked_interactions: getBaseTemplateTrackedInteractionCount(),
          /** DOM taps + keyboard submits (overlap with funnel when same click mirrored). */
          raw_ui_actions: rawUiInteractions,
          tti_ms: ttiMs,
          initial_landing_pathname: landing?.pathname ?? window.location.pathname,
          initial_full_href_saved: landing?.href ?? window.location.href.slice(0, 2048),
          spa_route_changes: spaTransitionsRef.current,
          viewport_w: typeof window.innerWidth === "number" ? window.innerWidth : null,
          viewport_h: typeof window.innerHeight === "number" ? window.innerHeight : null,
          viewport_dpr:
            typeof window.devicePixelRatio === "number"
              ? Math.round(window.devicePixelRatio * 100) / 100
              : null,
          fcp_ms: fcpMs,
          connection: conn,
          network_effective_type: netType,
          entry_type: entry.entry_type,
          entry_detail: entry.entry_detail,
          entry_label: entry.entry_label,
          browser_language: navLang || null,
        },
      };
    };

    const send = (exitReason: string, exitMeta?: { persisted_bfcache?: boolean }) => {
      if (sentRef.current) return;
      sentRef.current = true;
      mirrorFunnelEvent(buildPayload(exitReason, exitMeta));
    };

    const onPageHide = (ev: Event) => {
      const pe = ev as PageTransitionEvent;
      send(pe.persisted ? "pagehide_bf_cache" : "pagehide", { persisted_bfcache: !!pe.persisted });
    };
    const onBf = () => send("beforeunload");
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onBf);

    return () => {
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onBf);
    };
  }, [templateName, countryCode, language]);
}
